import { useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";
import {
  Upload,
  FileVideo,
  FileAudio,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  X,
  Film,
} from "lucide-react";

const MAX_FILE_BYTES = 200 * 1024 * 1024;

const ACCEPTED_AUDIO_EXTS = ["m4a", "mp3", "wav", "ogg", "aac", "opus", "flac"];
const ACCEPTED_VIDEO_EXTS = ["mp4", "mov", "webm", "m4v", "avi"];
const ALL_ACCEPTED_EXTS = [...ACCEPTED_VIDEO_EXTS, ...ACCEPTED_AUDIO_EXTS];

const ACCEPTED_AUDIO_MIMES = [
  "audio/mpeg", "audio/mp3", "audio/mp4", "audio/m4a", "audio/x-m4a",
  "audio/wav", "audio/wave", "audio/x-wav", "audio/ogg", "audio/opus",
  "audio/aac", "audio/flac", "audio/x-flac", "audio/webm",
];
const ACCEPTED_VIDEO_MIMES = [
  "video/mp4", "video/quicktime", "video/webm", "video/x-msvideo",
  "video/avi", "video/x-matroska", "video/x-m4v",
];

function isAcceptedType(file: File): { ok: boolean; isVideo: boolean } {
  const mime = file.type.toLowerCase();
  if (ACCEPTED_AUDIO_MIMES.includes(mime)) return { ok: true, isVideo: false };
  if (ACCEPTED_VIDEO_MIMES.includes(mime)) return { ok: true, isVideo: true };
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (ACCEPTED_VIDEO_EXTS.includes(ext)) return { ok: true, isVideo: true };
  if (ACCEPTED_AUDIO_EXTS.includes(ext)) return { ok: true, isVideo: false };
  return { ok: false, isVideo: false };
}

interface MediaUploadPanelProps {
  onSuccess?: () => void;
}

type UploadState = "idle" | "uploading" | "transcribing" | "success" | "error";

interface SuccessInfo {
  transcript: string;
  wordCount: number;
  source: string;
}

const BASE_URL = (import.meta as any).env?.BASE_URL ?? "/";

export function MediaUploadPanel({ onSuccess }: MediaUploadPanelProps) {
  const { sessionId } = useAppStore();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>("idle");  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");
  const [successInfo, setSuccessInfo] = useState<SuccessInfo | null>(null);

  const isProcessing = uploadState === "uploading" || uploadState === "transcribing";

  const reset = () => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    setUploadState("idle");
    setUploadProgress(0);
    setErrorMsg("");
    setSuccessInfo(null);
  };

  const processFile = useCallback((file: File) => {
    if (isProcessing) return;

    const { ok } = isAcceptedType(file);
    if (!ok) {
      setErrorMsg(
        `Unsupported format "${file.name.split(".").pop()?.toUpperCase()}". Accepted: ${ACCEPTED_VIDEO_EXTS.map((e) => e.toUpperCase()).join(", ")} (video) and ${ACCEPTED_AUDIO_EXTS.map((e) => e.toUpperCase()).join(", ")} (audio).`
      );
      setUploadState("error");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      const sizeMb = (file.size / 1024 / 1024).toFixed(1);
      setErrorMsg(`This file is ${sizeMb} MB — the limit is 200 MB. Try trimming it down or use a shorter clip.`);
      setUploadState("error");
      return;
    }

    setUploadState("uploading");
    setUploadProgress(0);
    setErrorMsg("");
    setSuccessInfo(null);

    const apiBase = BASE_URL.endsWith("/") ? BASE_URL.slice(0, -1) : BASE_URL;
    const formData = new FormData();
    formData.append("file", file);

    const xhr = new XMLHttpRequest();
    xhrRef.current = xhr;

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        const pct = Math.round((e.loaded / e.total) * 100);
        setUploadProgress(pct);
        if (pct >= 100) {
          setUploadState("transcribing");
        }
      }
    });

    xhr.addEventListener("load", () => {
      xhrRef.current = null;
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          setUploadState("success");
          setSuccessInfo({
            transcript: data.transcript ?? "",
            wordCount: data.wordCount ?? 0,
            source: data.source ?? "audio",
          });
          toast({
            title: "File processed!",
            description: `${data.wordCount} words transcribed and added to your voice profile.`,
          });
          onSuccess?.();
        } catch {
          setErrorMsg("Received an unexpected response. Please try again.");
          setUploadState("error");
        }
      } else {
        try {
          const errData = JSON.parse(xhr.responseText);
          const msg = errData.details || errData.error || "Could not process that file. Try a different format or shorter clip.";
          setErrorMsg(msg.replace(/^HTTP \d{3}[^:]*:\s*/, ""));
        } catch {
          setErrorMsg(`Upload failed (HTTP ${xhr.status}). Please try again.`);
        }
        setUploadState("error");
      }
    });

    xhr.addEventListener("error", () => {
      xhrRef.current = null;
      setErrorMsg("Network error — check your connection and try again.");
      setUploadState("error");
    });

    xhr.addEventListener("abort", () => {
      xhrRef.current = null;
    });

    xhr.open("POST", `${apiBase}/api/voice/upload-media`);
    xhr.setRequestHeader("x-session-id", sessionId);
    xhr.send(formData);
  }, [isProcessing, sessionId, toast, onSuccess]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (isProcessing) return;
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isProcessing) setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const isIdle = uploadState === "idle";
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Film className="w-4 h-4 text-primary" />
          Upload Video or Voice Memo
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Upload a video or audio file and we'll extract your voice automatically — perfect if you're on an older device or can't record live.
        </p>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !isProcessing && uploadState === "idle" && fileInputRef.current?.click()}        className={`relative rounded-2xl border-2 border-dashed transition-all ${
          isIdle
            ? isDragging
              ? "border-primary bg-primary/5 cursor-pointer"
              : "border-border bg-card/30 hover:border-primary/50 hover:bg-primary/5 cursor-pointer"
            : "border-border bg-card/20 cursor-default"
        }`}
      >
        <div className="p-6 flex flex-col items-center gap-4 text-center">
          <AnimatePresence mode="wait">
            {isIdle && (
              <motion.div
                key="idle"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex flex-col items-center gap-3"
              >
                <div className="flex gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileVideo className="w-6 h-6 text-primary" />
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileAudio className="w-6 h-6 text-primary" />
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {isDragging ? "Drop your file here" : "Drag & drop or tap to browse"}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Video: MP4, MOV, WebM, M4V, AVI
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Audio: M4A, MP3, WAV, OGG, AAC, OPUS, FLAC
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">Up to 200 MB</p>                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Upload className="w-4 h-4" />
                  Choose File
                </button>
              </motion.div>
            )}

            {uploadState === "uploading" && (
              <motion.div
                key="uploading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-3 w-full"
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-7 h-7 text-primary animate-spin" />
                </div>
                <div className="w-full max-w-xs">
                  <p className="text-sm font-medium text-foreground">Uploading your file…</p>
                  <p className="text-xs text-muted-foreground mt-1">{uploadProgress}% transferred</p>
                  <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-primary rounded-full"
                      animate={{ width: `${uploadProgress}%` }}
                      transition={{ ease: "linear" }}
                    />
                  </div>
                </div>
                <button
                  onClick={reset}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
              </motion.div>
            )}

            {uploadState === "transcribing" && (
              <motion.div
                key="transcribing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-3 w-full"
              >
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                  <Loader2 className="w-7 h-7 text-primary animate-spin" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Processing your audio…</p>
                  <p className="text-xs text-muted-foreground mt-1">Large files can take up to 2 minutes — hang tight!</p>
                </div>
              </motion.div>
            )}

            {uploadState === "success" && successInfo && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-3 w-full"
              >
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-7 h-7 text-emerald-400" />
                </div>
                <div className="w-full">
                  <p className="text-sm font-semibold text-emerald-400">
                    {successInfo.source === "uploaded-video" ? "Video" : "Audio"} transcribed — {successInfo.wordCount} words added
                  </p>
                  {successInfo.transcript && (
                    <p className="text-xs text-muted-foreground mt-2 line-clamp-3 italic text-left bg-secondary/30 rounded-lg px-3 py-2">
                      "{successInfo.transcript.slice(0, 200)}{successInfo.transcript.length > 200 ? "…" : ""}"
                    </p>
                  )}
                </div>
                <button
                  onClick={reset}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
                >
                  Upload another file
                </button>
              </motion.div>
            )}

            {uploadState === "error" && (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center gap-3 w-full"
              >
                <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-7 h-7 text-destructive" />
                </div>
                <div className="max-w-xs">
                  <p className="text-sm font-semibold text-destructive">Upload failed</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {errorMsg || "Could not process that file. Try a different format."}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { reset(); fileInputRef.current?.click(); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    Try another file
                  </button>
                  <button
                    onClick={reset}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-secondary text-foreground text-sm hover:bg-secondary/80 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" /> Dismiss
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ALL_ACCEPTED_EXTS.map((e) => `.${e}`).join(",")}
        className="hidden"
        onChange={handleFileChange}
        disabled={isProcessing}
      />
    </div>
  );
}
